import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import DupCodes from './DupCodes'
import MachineGrid from './MachineGrid'
import type { MachineGridHandle } from './MachineGrid'
import MasterEditMode from './MasterEditMode'
import type { ItemStart } from './MasterEditMode'
import MasterPullDialog from './MasterPullDialog'
import { exportMasterXlsx } from './masterExport'
import type { ExportLayout } from './masterExport'
import { masterIssues, tagLookup } from './masterView'
import type { SpecDraft } from './useSpecDraft'

// 명세서 [마스터데이터] 탭 — 인터페이스 ID별로 MES 전송코드와 그 값을 읽을 태그를 설계한다 (Edge 설치 전 키 목록 확정용).
// 디바이스 탭과 같은 짜임: 인터페이스 시트(인터페이스 ID·이름·설명) → 행 끝 [항목] 또는 머리의 [항목 편집] 으로 항목 편집(전송코드·태그).
// 신호·계측·파라미터 구분과 라인은 Edge 에 넣을 때 정한다. [엑셀 내보내기] 로 MES 팀·고객에게 넘길 키 목록.
export default function MasterTab({ spec, editKey, editStart, onEdit, handleRef, toolbarStart, onNotice, onError }: {
  spec: SpecDraft
  editKey: string | null | undefined // 항목 편집 중인 인터페이스 (undefined = 닫힘, null = 첫 인터페이스)
  editStart?: ItemStart | null // 항목 편집을 열 칸 (검색 결과)
  onEdit: (key: string | null | undefined) => void
  toolbarStart?: ReactNode // 툴바 왼쪽 — 검색·되돌리기 (디바이스 탭과 같은 자리)
  handleRef: (handle: MachineGridHandle | null) => void
  onNotice: (message: string) => void
  onError: (message: string) => void
}) {
  const { draft, setDraft, locate, viewers, me } = spec
  const others = useMemo(() => viewers.filter(v => v.connectionId !== me), [viewers, me])
  const lookup = useMemo(() => tagLookup(draft.devices), [draft.devices])
  const issues = useMemo(() => masterIssues(draft.machines, lookup), [draft.machines, lookup])

  useEffect(() => () => locate(null), [locate])

  const items = Object.values(issues.machine)
  const itemCount = items.reduce((n, m) => n + m.items, 0)
  const unbound = items.reduce((n, m) => n + m.unbound, 0)
  const missing = items.reduce((n, m) => n + m.missing, 0)

  const [exporting, setExporting] = useState(false)
  const [pulling, setPulling] = useState(false)
  const exportXlsx = async (layout: ExportLayout) => {
    setExporting(false)
    try { await exportMasterXlsx(draft, layout) } catch { onError('엑셀 파일을 만들지 못했습니다.') }
  }

  return (
    <>
      {/* 위 여백(pt-3)은 스크롤 상자가 아니라 여기 — 상자 padding 은 sticky 를 그만큼 내려 셀이 비친다 (DeviceSpecDetail 과 같게) */}
      <div className="sticky top-0 z-20 bg-canvas flex flex-wrap items-center gap-2 pt-3 pb-3">
        {toolbarStart}
        <span className="text-xs text-gray-500">
          인터페이스 {draft.machines.length} · 전송코드 {itemCount}
          {unbound > 0 && ` · 태그 미연결 ${unbound}`}
          {missing > 0 && <span className="text-red-600 font-medium"> · 명세에 없는 태그 {missing}</span>}
        </span>
        {issues.dupPlaces.length > 0 && <span className="text-xs text-gray-500">· <DupCodes places={issues.dupPlaces} onPick={onEdit} /></span>}
        <button onClick={() => setPulling(true)} title="Edge에 설정된 설비(인터페이스)와 전송코드를 명세서로 가져옵니다"
          className="ml-auto border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50">Edge → 명세 가져오기</button>
        <button onClick={() => setExporting(true)} disabled={draft.machines.length === 0} title="공유용 엑셀 파일로 내보냅니다"
          className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 disabled:opacity-40">엑셀 내보내기</button>
      </div>

      {editKey === undefined && (
        <MachineGrid machines={draft.machines} issues={issues} viewers={others}
          onChange={machines => setDraft(d => ({ ...d, machines }))}
          onEditItems={onEdit} onNotice={onNotice} handleRef={handleRef}
          onLocate={(device, field, editing) => locate(device ? { mode: 'machines', device, field, editing } : null)} />
      )}
      <p className="mt-2 px-1 text-xs text-gray-500">
        빈 행에 입력하면 인터페이스가 추가됩니다. [항목]을 눌러 전송코드와 태그를 입력하세요. 우클릭해서 복제하면 항목까지 함께 복사됩니다.
      </p>

      {pulling && (
        <MasterPullDialog machines={draft.machines} devices={draft.devices} onClose={() => setPulling(false)}
          onApply={(machines, summary) => {
            setDraft(d => ({ ...d, machines }))
            setPulling(false)
            onNotice(`${summary}. (Ctrl+Z 로 되돌리기)`)
          }} />
      )}
      {exporting && <ExportDialog count={draft.machines.length} onExport={exportXlsx} onClose={() => setExporting(false)} />}

      {editKey !== undefined && (
        <MasterEditMode spec={spec} machineKey={editKey} start={editStart} lookup={lookup} issues={issues} onClose={() => onEdit(undefined)} onNotice={onNotice} />
      )}
    </>
  )
}

// 엑셀 내보내기 — 시트 나누는 방식 고르기 (기본: 한 시트)
function ExportDialog({ count, onExport, onClose }: { count: number; onExport: (layout: ExportLayout) => void; onClose: () => void }) {
  const [layout, setLayout] = useState<ExportLayout>('single')
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter') onExport(layout) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [layout, onClose, onExport])
  const options: { value: ExportLayout; label: string; detail: string }[] = [
    { value: 'single', label: '한 시트에 모두', detail: '모든 인터페이스를 표 하나로 정리합니다' },
    { value: 'perInterface', label: '인터페이스마다 시트', detail: `인터페이스 ID별로 시트를 나눕니다 (${count}개)` },
  ]
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onMouseDown={onClose}>
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-sm" onMouseDown={e => e.stopPropagation()}>
        <div className="px-5 pt-4 pb-2">
          <h3 className="font-semibold text-gray-800">엑셀 내보내기</h3>
          <p className="mt-1 text-xs text-gray-500">인터페이스 ID, 데이터 항목, 전송코드를 표로 만듭니다.</p>
        </div>
        <div className="px-5 py-2 space-y-2">
          {options.map(o => (
            <label key={o.value} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition ${
              layout === o.value ? 'border-blue-400 bg-blue-50/60' : 'border-gray-200 hover:bg-gray-50'}`}>
              <input type="radio" name="layout" checked={layout === o.value} onChange={() => setLayout(o.value)} className="mt-0.5 accent-blue-600" />
              <span>
                <span className="block text-sm text-gray-800">{o.label}</span>
                <span className="block text-xs text-gray-500">{o.detail}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4">
          <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">취소</button>
          <button onClick={() => onExport(layout)} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover">내보내기</button>
        </div>
      </div>
    </div>
  )
}
